import { PreviewComponent } from '@/services/utils/basePreview'
import { metadataLabelValues } from '@/shared/helpers/metadataLabelValues'
import {
  CollectionStrategy,
  ICollection,
  StyleType
} from '@/modules/page/collection/types/collectionTypes'
import { ContentFormatter } from '@/shared/helpers/contentFormatter'
import i18n from '@/plugins/i18n'
import { getCurrencySymbol } from '@/shared/helpers/getCurrencySymbol'
import { createImagePath } from '@/shared/helpers/createImagePath'

/**
 * dynamic component generator method
 * @param collection is ICollection interface type which
 * @return An Array of IPreviewComponent collection type
 */
export function collectionPreviewComponents(
  collection: ICollection
): PreviewComponent<ICollection>[] {
  const isManual = collection.collectionStrategy === CollectionStrategy.manual

  const isAutomatic =
    collection.collectionStrategy === CollectionStrategy.automatic

  // Check if we should display single images (banner, half_banner, navigation)
  const isDisplaySingleImage =
    collection.styleType === StyleType.banner ||
    collection.styleType === StyleType.halfBanner ||
    collection.styleType === StyleType.navigation

  return [
    // Large Image (Web) - for banner, half_banner, navigation
    PreviewComponent.photo<ICollection>(
      'largeImage',
      collection.largeImage ? createImagePath(collection.largeImage) : ''
    )
      .setTitle('largeScreen')
      .setHidden(!isDisplaySingleImage),
    // Mobile Image - for banner, half_banner, navigation
    PreviewComponent.photo<ICollection>(
      'image',
      collection.image ? createImagePath(collection.image) : ''
    )
      .setTitle('smallScreen')
      .setHidden(!isDisplaySingleImage),
    // Multiple images for slider
    PreviewComponent.multiPhoto<ICollection>(
      'images',
      collection.images
        ? (collection.images as { id: number; image: string }[]).map(img =>
            createImagePath(img.image)
          )
        : []
    ).setHidden(collection.styleType !== StyleType.slider),
    PreviewComponent.labelValue<ICollection>(
      'title',
      'title',
      collection.title
    ).setCol('12'),
    PreviewComponent.labelValue<ICollection>(
      'description',
      'description',
      collection.description
    ).setCol('12'),
    PreviewComponent.labelValue<ICollection>(
      'styleType',
      'styleType',
      ContentFormatter.$customizeText(collection.styleType)
    ),
    PreviewComponent.labelValue<ICollection>(
      'collectionStrategy',
      'collectionStrategy',
      ContentFormatter.$customizeText(collection.collectionStrategy)
    ).setHidden(
      collection.styleType === StyleType.navigation ||
        collection.styleType === StyleType.slider ||
        collection.styleType === StyleType.brand
    ),
    PreviewComponent.labelValue<ICollection>(
      'buttonText',
      'buttonText',
      collection.buttonText
    ).setHidden(
      !(
        collection.styleType === StyleType.banner ||
        collection.styleType === StyleType.halfBanner
      )
    ),
    PreviewComponent.labelValue<ICollection>(
      'destination',
      'destination',
      ContentFormatter.$customizeText(collection.destination)
    ).setHidden(!(collection.styleType === StyleType.navigation)),
    PreviewComponent.labelValue<ICollection>(
      'imagePosition',
      'imagePosition',
      ContentFormatter.$customizeText(collection.imagePosition)
    ).setHidden(!(collection.styleType === StyleType.halfBanner)),
    PreviewComponent.table<ICollection>(
      'filterKwargs',
      [
        {
          text: i18n.t('header.fieldName'),
          value: 'fieldName',
          key: 'fieldName',
          formatValue: 'customizeText'
        },
        {
          text: i18n.t('header.lookupExpressions'),
          value: 'lookupExpression',
          key: 'lookupExpression',
          formatValue: 'text'
        },
        {
          text: i18n.t('header.value'),
          value: 'value',
          key: 'value',
          formatValue: 'text'
        }
      ],
      collection.filterKwargs
    )
      .setProp({ class: 'col-12' })
      .setTitle('filteredKwargs')
      .setHidden(!isAutomatic),
    PreviewComponent.table<ICollection>(
      'categories',
      [
        {
          text: 'image',
          value: 'image',
          key: 'image',
          formatValue: 'file',
          io: 'input',
          formatType: 'photo',
          cellProps: item => {
            return {
              src: item.image ? createImagePath(item.image) : '',
              height: 40,
              width: 40,
              contain: true
            }
          },
          width: '80px'
        },
        {
          text: i18n.t('header.name'),
          value: 'name',
          key: 'name',
          formatValue: 'text'
        },
        {
          text: i18n.t('header.categoryType'),
          value: 'categoryType',
          key: 'categoryType',
          formatValue: 'text'
        }
      ],
      collection.categories
    )
      .setProp({ class: 'col-12' })
      .setTitle('categories')
      .setHidden(!(collection.styleType === StyleType.category && isManual)),
    PreviewComponent.table<ICollection>(
      'products',
      [
        {
          text: 'images',
          value: 'images',
          key: 'images',
          formatValue: 'file',
          io: 'input',
          formatType: 'photo',
          cellProps: item => {
            const imageUrl =
              typeof item.images[0] === 'string' ? item.images[0] : ''
            return {
              src: imageUrl ? createImagePath(imageUrl) : '',
              height: 40,
              width: 40,
              contain: true
            }
          },
          width: '80px'
        },
        {
          text: i18n.t('header.name'),
          value: 'name',
          key: 'name',
          formatValue: 'text'
        },
        {
          text: `${i18n.t('header.salePrice')} (${getCurrencySymbol()})`,
          value: 'salePrice',
          key: 'salePrice',
          formatValue: 'currency'
        }
      ],
      collection.products
    )
      .setProp({ class: 'col-12' })
      .setTitle('products')
      .setHidden(
        !(
          (collection.styleType === StyleType.product ||
            collection.styleType === StyleType.banner ||
            collection.styleType === StyleType.halfBanner) &&
          isManual
        )
      ),
    PreviewComponent.table<ICollection>(
      'brands',
      [
        {
          text: 'image',
          value: 'image',
          key: 'images',
          formatValue: 'file',
          io: 'input',
          formatType: 'photo',
          cellProps: item => {
            return {
              src: item.image ? createImagePath(item.image) : '',
              height: 40,
              width: 40,
              contain: true
            }
          },
          width: '80px'
        },
        {
          text: i18n.t('header.name'),
          value: 'name',
          key: 'name',
          formatValue: 'text'
        }
      ],
      collection.brands
    )
      .setProp({ class: 'col-12' })
      .setTitle('brands')
      .setHidden(collection.styleType !== StyleType.brand),
    ...metadataLabelValues(collection)
  ]
}
